import { jwtDecode } from 'jwt-decode';
import React, { createContext, useContext, useEffect, useState } from 'react';
import axios, { setAuthContext } from '../utils/axiosConfig';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null); // Store in memory instead of localStorage
  const [authChecked, setAuthChecked] = useState(false); // Prevent multiple auth checks

  useEffect(() => {
    if (!authChecked) {
      checkAuth();
    }
  }, [authChecked]);

  const checkAuth = async () => {
    // Prevent multiple simultaneous auth checks
    if (authChecked) {
      return isAuthenticated;
    }

    console.log('checkAuth - Starting auth check');
    setAuthChecked(true);

    // If we have an access token in memory, validate it
    if (accessToken) {
      try {
        const decoded = jwtDecode(accessToken);
        console.log('checkAuth - Decoded token:', decoded);

        // Check if token is expired
        const currentTime = Date.now() / 1000;
        console.log('checkAuth - Current time:', currentTime);
        console.log('checkAuth - Token expiry:', decoded.exp);

        // Add 60 seconds of clock skew tolerance
        if (decoded.exp < currentTime - 60) {
          console.log('checkAuth - Token expired, attempting refresh');
          try {
            // Try to refresh the token
            await refreshAccessToken();
            console.log('checkAuth - Token refresh successful');
            return true;
          } catch (refreshError) {
            console.error('checkAuth - Token refresh failed:', refreshError);
            clearAuthState();
            return false;
          }
        }

        setUser(decoded);
        setIsAuthenticated(true);
        setLoading(false);
        console.log('checkAuth - Auth successful');
        return true;
      } catch (error) {
        console.error('checkAuth - Error:', error);
        clearAuthState();
        return false;
      }
    }

    // No access token in memory, try to get one using refresh token (cookie)
    console.log('checkAuth - No access token in memory, attempting refresh');
    try {
      await refreshAccessToken();
      console.log('checkAuth - Successfully obtained access token via refresh');
      return true;
    } catch (refreshError) {
      console.log('checkAuth - No valid refresh token available');
      clearAuthState();
      return false;
    }
  };

  const clearAuthState = () => {
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
    // Clear userID from localStorage (still used by some components)
    localStorage.removeItem('userID');
  };

  const logout = async () => {
    console.log('Logout initiated');

    // Call backend logout to clear refresh token cookie BEFORE clearing local state
    try {
      if (accessToken) {
        await axios.post('/api/v1/auth/logout');
        console.log('Backend logout successful');
      }
    } catch (error) {
      console.error('Error during backend logout:', error);
      // Continue with local logout even if backend call fails
    }

    // Clear local state
    clearAuthState();
    setAuthChecked(false); // Allow new auth checks
    console.log('Local logout completed');
  };

  const refreshAccessToken = async () => {
    try {
      console.log('Starting token refresh...');

      const response = await axios.post('/api/v1/auth/refresh');

      console.log('Got new access token from refresh endpoint');

      // Store the access token in memory
      if (response.data.access_token) {
        setAccessToken(response.data.access_token);

        try {
          const decoded = jwtDecode(response.data.access_token);
          setUser(decoded);
          setIsAuthenticated(true);
          setLoading(false);
        } catch (e) {
          console.error('Error decoding access token after refresh:', e);
          throw e;
        }
      } else {
        throw new Error('No access token in refresh response');
      }

      return response.data.access_token;
    } catch (error) {
      console.error('Error refreshing token:', error);
      // Don't call logout here to prevent loops - just throw
      throw error;
    }
  };

  const setTokens = (newAccessToken) => {
    console.log('Setting new access token');
    console.log('Access token (first 50 chars):', newAccessToken?.substring(0, 50) + '...');
    console.log('Access token length:', newAccessToken?.length);

    // Store access token in memory
    setAccessToken(newAccessToken);

    // Decode and set user info
    try {
      const decoded = jwtDecode(newAccessToken);
      console.log('Decoded token payload:', decoded);
      setUser(decoded);
      setIsAuthenticated(true);
      setLoading(false);
      setAuthChecked(true); // Mark auth as checked
      console.log('Successfully set tokens and auth state');
    } catch (e) {
      console.error('Error decoding access token:', e);
      clearAuthState();
    }
  };

  // Create auth context object after all functions are defined
  const authContextValue = {
    isAuthenticated,
    user,
    loading,
    accessToken,
    checkAuth,
    logout,
    refreshAccessToken,
    setTokens,
  };

  // Set auth context for axios when it changes
  useEffect(() => {
    setAuthContext(authContextValue);
  }, [accessToken, isAuthenticated]);

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
